import React from 'react';
import { Shield, Target, Eye, CheckCircle, Award, Users, Lightbulb, FileCheck, UserCheck, Wrench } from 'lucide-react';
import { aboutContent, values, industries } from '../data/mockData';
import SEO from '../components/SEO';

const About = () => {
  return (
    <div className="min-h-screen bg-[#f9fafb] overflow-hidden">
      <SEO
        title="About Stallion Bulktech | Industrial Dust & Wear Solution Experts"
        description="Stallion Bulktech specializes in industrial dust suppression, machine guarding, and wear resistance solutions for mining, cement, and bulk material handling industries."
        path="/about"
      />
      {/* Hero Section */}
      <section className="relative pt-24 pb-12 md:pt-32 md:pb-20 overflow-hidden">
        {/* Animated Background Layers */}
        <div className="absolute inset-0 bg-gradient-to-br from-white via-cyan-50/30 to-blue-50/30">
          <div className="absolute top-20 right-20 w-96 h-96 bg-gradient-to-br from-cyan-400/20 to-blue-500/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-gradient-to-br from-blue-400/20 to-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-cyan-50 border border-cyan-200 rounded-full mb-6">
              <Shield className="h-4 w-4 text-cyan-600" />
              <p className="text-cyan-600 text-sm font-medium">About Stallion Bulktech</p>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              {aboutContent.title}
            </h1>
            <div className="max-w-4xl mx-auto space-y-5 md:space-y-6">
              <p className="text-base md:text-lg text-gray-600 leading-relaxed">
                {aboutContent.description}
              </p>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed">
                {aboutContent.description2}
              </p>
            </div>
          </div>

          {/* ISO Certification Badge */}
          <div className="flex justify-center">
            <div className="bg-white/90 backdrop-blur-xl border border-gray-200 rounded-2xl p-6 inline-flex items-center space-x-4 shadow-lg hover:shadow-xl transition-all">
              <Award className="h-12 w-12 text-cyan-600" />
              <div>
                <div className="text-gray-900 font-bold text-lg">ISO 9001:2015 Certified</div>
                <div className="text-gray-600 text-sm">Quality Management System</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-12 md:py-20 bg-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 left-0 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Vision */}
            <div className="group relative h-full">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              
              <div className="relative h-full bg-white/90 backdrop-blur-xl border border-gray-200 rounded-2xl p-8 hover:border-cyan-300 transition-all duration-500 transform group-hover:-translate-y-2 group-hover:shadow-2xl">
                <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Eye className="h-7 w-7 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-cyan-600 transition-colors">Our Vision</h2>
                <p className="text-gray-600 leading-relaxed">
                  {aboutContent.vision}
                </p>
              </div>
            </div>

            {/* Mission */}
            <div className="group relative h-full">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              
              <div className="relative h-full bg-white/90 backdrop-blur-xl border border-gray-200 rounded-2xl p-8 hover:border-cyan-300 transition-all duration-500 transform group-hover:-translate-y-2 group-hover:shadow-2xl">
                <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Target className="h-7 w-7 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-cyan-600 transition-colors">Our Mission</h2>
                <p className="text-gray-600 leading-relaxed">
                  {aboutContent.mission}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-[#f9fafb] to-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-full mb-4">
              <Award className="h-4 w-4 text-blue-600" />
              <span className="text-blue-600 text-sm font-medium">Our Values</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Why Choose Us</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our commitment to excellence is reflected in everything we do
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const icons = {
                'Quality': Award,
                'Flexibility': Wrench,
                'Experience': Users,
                'Expertise': Lightbulb,
                'Compliance': FileCheck,
                'Client Satisfaction': UserCheck
              };
              const Icon = icons[value.title] || Shield;

              return (
                <div
                  key={value.title}
                  className="group relative"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                  
                  <div className="relative h-full bg-gradient-to-br from-white to-gray-50/50 border border-gray-200 rounded-2xl p-8 hover:border-cyan-300 transition-all duration-500 transform hover:-translate-y-3 hover:shadow-2xl">
                    <div className="relative w-12 h-12 mb-6">
                      <div className="absolute inset-0 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl blur opacity-30 group-hover:opacity-60 transition-opacity animate-pulse" />
                      <div className="relative w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-lg">
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-cyan-600 transition-colors">{value.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{value.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How We Do It */}
      <section className="py-12 md:py-20 bg-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-white to-gray-50/50 border border-gray-200 rounded-2xl p-6 md:p-12 shadow-xl">
            <div className="text-center mb-10 md:mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                We Guide You to The Most Adequate System and Material Handling Solutions
              </h2>
              <p className="text-lg md:text-xl text-cyan-600 font-semibold">How We Do It?</p>
            </div>

            <div className="max-w-4xl mx-auto space-y-5 md:space-y-6 text-base md:text-lg text-gray-600 leading-relaxed text-center">
              <p>
                With a team of experienced engineers, we define the right machine guarding and material handling solutions for your facility. We evaluate wear zones, dust exposure points, and access requirements to design solutions that are practical, compliant, and built for real operating conditions.
              </p>
              <p>
                Choosing Stallion Bulktech means working directly with the manufacturer from evaluation through on-site installation and ongoing support. Our integrated machine guarding and material handling solutions protect equipment, reduce maintenance, and keep operations running consistently.
              </p>
              <div className="text-center pt-8">
                <a
                  href="/contact"
                  className="group relative inline-flex items-center space-x-2"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 to-blue-600 rounded-xl blur opacity-50 group-hover:opacity-100 transition-opacity" />
                  <div className="relative flex items-center space-x-2 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-xl px-8 py-4 transform group-hover:scale-105 transition-all shadow-lg">
                    <span>Request a Custom Solution</span>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-[#f9fafb] to-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-purple-50 border border-purple-200 rounded-full mb-4">
              <Shield className="h-4 w-4 text-purple-600" />
              <span className="text-purple-600 text-sm font-medium">Industry Expertise</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Industries We Serve</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Providing safety solutions across diverse industrial sectors
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {industries.map((industry, index) => (
              <div
                key={industry.name}
                className="group relative"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="relative bg-white border border-gray-200 rounded-2xl overflow-hidden hover:border-cyan-300 transition-all duration-500 transform hover:-translate-y-1 hover:shadow-xl">
                  <div className="aspect-square overflow-hidden bg-gradient-to-br from-gray-100 to-gray-50">
                    <img
                      src={industry.image}
                      alt={industry.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4 text-center bg-white">
                    <h3 className="text-gray-900 font-semibold group-hover:text-cyan-600 transition-colors">
                      {industry.name}
                    </h3>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
